/**
 * The template's regions, described for somebody who has never seen one.
 *
 * The measured map calls a rectangle `leftLimb.front`, which is right
 * internally and wrong on a page. Two things have to be resolved before a
 * person can read it: which garment it is (the same rectangle wraps an arm or a
 * leg), and what a limb's `left` and `right` actually are — they are its
 * **inner** and **outer** faces, not sides. `selection.ts` owns both answers;
 * this composes them with the part name.
 *
 * Pure, because these numbers go on the homepage as claims about Roblox's
 * format and being confidently wrong there is worse than saying nothing.
 */
import { CANVAS, type GarmentType, type RegionId } from "./format";
import { REGIONS } from "./regions";
import { faceLabel, limbNoun } from "./selection";

export interface RegionFacts {
  id: RegionId;
  /** "Left sleeve · front", garment-aware. */
  label: string;
  /** Which of the three part groups it belongs to. */
  group: "torso" | "left" | "right";
}

export function regionFacts(id: RegionId, garment: GarmentType): RegionFacts {
  const [part] = id.split(".") as [string];
  const limb = limbNoun(garment === "pants" ? "pants" : "shirt");
  const where = part === "torso" ? "Torso" : part === "leftLimb" ? `Left ${limb}` : `Right ${limb}`;

  /*
   * The face word comes from `faceLabel`, not from a table here.
   *
   * A limb's `left` and `right` are **inner** and **outer**, not sides: on the
   * right limb, `left` is the face pressed against the torso — 0.0026 studs
   * from it and hidden by it — so "left side" is actively misleading, and it is
   * the very face the ghosting exists to reveal. `selection.ts` worked this out
   * first and explains it at length; a simpler table here got it wrong.
   */
  return {
    id,
    label: `${where} · ${faceLabel(id).toLowerCase()}`,
    group: part === "torso" ? "torso" : part === "leftLimb" ? "left" : "right",
  };
}

/**
 * The share of the canvas that never appears on the avatar.
 *
 * Derived from the same measured rectangles the map draws, so the headline
 * number and the picture can never disagree — which they would the moment
 * somebody typed "54.9" into the copy by hand.
 */
export function deadSpacePct(): number {
  return deadSpacePctOf(Object.values(REGIONS), CANVAS);
}

/**
 * The same calculation, over any map.
 *
 * Split out because the version above cannot be tested. Hardcoding `return
 * 54.9` passes every assertion you can write against the real region map — it
 * *is* the right answer today — so the only way to prove the number is derived
 * rather than typed is to run the arithmetic over a map whose answer is
 * something else. A mutation test caught exactly that.
 */
export function deadSpacePctOf(
  rects: readonly { w: number; h: number }[],
  canvas: { width: number; height: number },
): number {
  const area = canvas.width * canvas.height;
  if (area <= 0) return 0;
  const mapped = rects.reduce((sum, r) => sum + r.w * r.h, 0);
  return Math.round(((area - mapped) * 1000) / area) / 10;
}

/**
 * Which faces the artist left blank, named for a person.
 *
 * A real mistake people ship: the back of a shirt, or the inner sleeves,
 * forgotten because you never rotate the avatar to look. An earlier
 * `namedEmptyRegions` in `coverage.ts` answered a similar question but labelled
 * with the raw region naming, which said "left limb" — wrong half the time once
 * pants exist. It has been removed in favour of this.
 *
 * Returns null when there is nothing worth saying:
 *
 *   - **none empty** — a complete design needs no note.
 *   - **all empty** — that is an untouched template, not a mistake, and
 *     listing eighteen faces says less than saying nothing.
 */
export function emptyFacesNote(
  empty: readonly RegionId[],
  total: number,
  garment: GarmentType,
): string | null {
  if (empty.length === 0 || empty.length >= total) return null;

  const names = empty.map((id) => regionFacts(id, garment).label.toLowerCase());
  // Three is where a list stops being readable at a glance and becomes a wall.
  const shown = names.slice(0, 3);
  const rest = names.length - shown.length;

  const list =
    shown.length === 1
      ? shown[0]
      : `${shown.slice(0, -1).join(", ")} and ${shown[shown.length - 1]}`;

  const tail = rest > 0 ? `, and ${rest} more` : "";
  return `Nothing painted on ${list}${tail}.`;
}
